import { Dir } from '@angular/cdk/bidi';
import { Component, computed, inject, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MAT_MENU_DEFAULT_OPTIONS, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { AuthService } from '@common/services/authentication/auth.service';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { PersonalSettings, PersonalSettingsService } from '@common/services/personal-settings-service';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { AppTheme } from '@src/suite/app.theme';
import { EnpsComponent } from '@src/suite/common/components/base/enps-component';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { GlobalTablesActions } from '@src/suite/common/store/global-tables/global-tables.actions';
import { selectPersonalTimeZone, selectPlaybookHelpUrl, selectStorytellingAppNavigation } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { HelloResultsActions } from '@src/suite/common/store/hello-results/hello-results.actions';
import {
    selectDarkMode,
    selectDateLocale,
    selectDayOfWeek,
    selectPSTNotificationSettings,
    selectRtl,
    selectSupervisor,
    selectUserID,
    selectUserStaffInfo,
} from '@src/suite/common/store/hello-results/hello-results.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { applyPersonalTimeZone } from '@src/suite/common/utils/date-utils';
import { updateLuxonWeekSettings } from '@src/suite/common/utils/util';
import { DiscoverAdminSettingsDialogComponent } from '@src/suite/discover/components/discover-admin-settings-dialog/discover-admin-settings-dialog';
import { Subject } from 'rxjs';

import { AvatarBigComponent } from '../../../common/components/avatar/avatar-big';
import { AvatarComponent } from '../../../common/components/avatar/avatar-upload';
import { CheckBoxComponent } from '../../../common/controls/check-box/check-box';
import { ComboBoxComponent } from '../../../common/controls/combo-box/combo-box';
import { TextFieldComponent } from '../../../common/controls/text-field/text-field';
import { ToggleControlComponent } from '../../../common/controls/toggle-button/toggle-control';
import { ClickStopPropagationDirective } from '../../../common/directives/stop-propagation';
import { TopBarUserMenuDialogAboutComponent } from './top-bar-user-menu-dialog-about';

type MenuCloseReason = void | 'click' | 'keydown' | 'tab';

@Component({
    selector: 'st-top-bar-user-menu',
    imports: [AvatarBigComponent, AvatarComponent, CheckBoxComponent, ClickStopPropagationDirective, ComboBoxComponent, Dir, MatIconModule, MatMenuModule, TextFieldComponent, ToggleControlComponent],
    // changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './top-bar-user-menu.html',
    styleUrl: './top-bar-user-menu.scss',
    providers: [
        {
            provide: MAT_MENU_DEFAULT_OPTIONS,
            useValue: { overlayPanelClass: 'st-user-menu' },
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class TopBarUserMenuComponent extends EnpsComponent {
    store: Store = inject(Store);
    appService = inject(AppService);
    visualStateService = inject(VisualStateService);
    authService = inject(AuthService);
    router = inject(Router);
    nomWebServicesJson = inject(NomWebServicesJson);
    navigationService = inject(NavigationService);
    languageSettings = this.store.selectSignal(selectI18N);
    globalTablesService = inject(GlobalTablesService);
    personalSettingsService = inject(PersonalSettingsService);
    dialog = inject(MatDialog);
    themeService = inject(AppTheme);
    clickSubject = new Subject<Event>();

    settingsMenuTemplate = [{ OVERALL: 'OVERALL' }, { NOTIFICATIONS: 'NOTIFICATIONS' }, { REGIONANDLANGUAGE: 'REGIONANDLANGUAGE' }];

    readonly commonTimeZones = signal<TimeZone[]>([]);
    readonly ianaTimeZones = signal<TimeZone[]>([]);
    readonly languages = signal<ENPSLanguage[]>([]);
    readonly menuTrigger = viewChild(MatMenuTrigger);
    readonly menuOpened = output<void>();
    readonly closed = output<MenuCloseReason>();
    readonly timeZoneValue = signal<LookupValue | null>(null);
    readonly languageValue = signal<LookupValue | null>(null);
    readonly currentMenu = signal<string>(this.settingsMenuTemplate[0].OVERALL);
    readonly dayOfWeek = this.store.selectSignal(selectDayOfWeek);
    readonly personalTimeZone = this.store.selectSignal(selectPersonalTimeZone);
    readonly personalTimeZoneDisplay = this.store.selectSignal(selectPersonalTimeZone);
    readonly dateLocale = this.store.selectSignal(selectDateLocale);
    readonly userId = this.store.selectSignal(selectUserID);
    readonly userStaffInfo = this.store.selectSignal(selectUserStaffInfo);
    readonly navigation = this.store.selectSignal(selectStorytellingAppNavigation);
    readonly playbookHelpUrl = this.store.selectSignal(selectPlaybookHelpUrl);
    readonly supervisor = this.store.selectSignal(selectSupervisor);
    readonly rtl = this.store.selectSignal(selectRtl);
    readonly pstNotificationSettings = this.store.selectSignal(selectPSTNotificationSettings);
    readonly selectDarkMode = this.store.selectSignal(selectDarkMode);

    readonly weekDays = signal<{ id: number; title: string }[]>([
        { id: 0, title: this.languageSettings().PSTWebSunday },
        { id: 1, title: this.languageSettings().PSTWebMonday },
        { id: 2, title: this.languageSettings().PSTWebTuesday },
        { id: 3, title: this.languageSettings().PSTWebWednesday },
        { id: 4, title: this.languageSettings().PSTWebThursday },
        { id: 5, title: this.languageSettings().PSTWebFriday },
        { id: 6, title: this.languageSettings().PSTWebSaturday },
    ]);
    readonly dayOfWeekValue = computed(() => {
        return this.weekDays().find(x => x.id === this.dayOfWeek()) || this.weekDays()[0];
    });

    constructor() {
        super();
        this.commonTimeZones.set(this.globalTablesService.CommonTimeZones);
        this.ianaTimeZones.set(this.globalTablesService.IanaTimeZones);
        this.languages.set(this.globalTablesService.Languages);
        this.languageValue.set({ id: this.globalTablesService.PersonalLanguage?.id, title: this.globalTablesService.PersonalLanguage?.title });
        this.hasTimeZoneValue();
    }

    readonly timeZoneLabel = computed(() => {
        return this.languageSettings().PSTWebTimeZoneLabel + (this.personalTimeZone() ? ` (${this.personalTimeZone().id})` : ` (${this.globalTablesService.ClientTimeZone?.id})`);
    });

    readonly userDisplayName = computed((): string => {
        return this.userStaffInfo().DisplayName;
    });

    readonly userDepartment = computed((): string => {
        return this.userStaffInfo().Program;
    });

    readonly hasCastNavigation = computed((): boolean => {
        return this.navigation().some(x => x.Product === 'cast');
    });
    readonly isRTL = computed((): 'rtl' | 'ltr' => {
        return this.rtl() ? 'rtl' : 'ltr';
    });

    readonly rtlLabel = computed(() => {
        return this.rtl() ? this.languageSettings().PSTWebRTLOriantation : this.languageSettings().PSTWebLTROriantation;
    });

    readonly shouldShowPublicationChannels = computed(() => {
        return this.supervisor() && this.hasCastNavigation() && !this.visualStateService.isLessThanSmall();
    });

    readonly hasAdminSettings = computed(() => {
        // TODO check for the admin settings
        return this.supervisor() && !this.visualStateService.isLessThanSmall();
    });

    private hasTimeZoneValue() {
        if (this.globalTablesService.PersonalTimeZoneDisplay === null) {
            this.timeZoneValue.set(null);
        } else {
            this.timeZoneValue.set({ id: this.globalTablesService.PersonalTimeZoneDisplay.id, title: this.globalTablesService.PersonalTimeZoneDisplay.title });
        }
    }

    public openFile(event: Event) {
        this.clickSubject.next(event);
    }

    public openMenu(): void {
        this.menuOpened.emit();
    }

    public closeMenu(): void {
        this.closed.emit();
    }

    public async showAbout(event) {
        this.dialog.closeAll();
        this.dialog.open(TopBarUserMenuDialogAboutComponent, {
            hasBackdrop: true,
        });
        this.navigationService.showAbout(event);
    }

    public async logoutUser() {
        this.themeService.updateSystemColorMode();
        await this.authService.logout();
    }

    public async updatePersonalSettings() {
        const settings: PersonalSettings = {
            DefaultTimeZone: this.nomWebServicesJson.HelloResults.PersonalTimeZone,
            DefaultLanguage: this.nomWebServicesJson.HelloResults.PersonalLanguage,
            Rtl: this.nomWebServicesJson.HelloResults.Rtl,
            DayOfWeek: this.nomWebServicesJson.HelloResults.DayOfWeek,
        };
        await this.personalSettingsService.updatePersonalSettings(settings);
        localStorage.setItem('GlobalsHelloResults', JSON.stringify(this.nomWebServicesJson.HelloResults));
    }

    public showRegionAndLanguageSettings() {
        this.currentMenu.set(this.settingsMenuTemplate[2].REGIONANDLANGUAGE);
    }

    public showManageTeamMembers() {
        this.router.navigate(['/PST', this.appService.applicationName, 'manage_team_members']);
    }

    public showManagePublicationChannels() {
        this.router.navigate(['/PST', this.appService.applicationName, 'manage_publication_channels']);
    }
    public showNotificationSettings() {
        this.currentMenu.set(this.settingsMenuTemplate[1].NOTIFICATIONS);
    }

    public async updateNotificationSettings() {
        await this.personalSettingsService.updatePersonalNotificationSettings(this.pstNotificationSettings());
        localStorage.setItem('GlobalsHelloResults', JSON.stringify(this.nomWebServicesJson.HelloResults));
    }

    readonly isDisabled = computed(() => {
        return !this.pstNotificationSettings().Phone.Recieve;
    });

    public backButton() {
        this.currentMenu.set(this.settingsMenuTemplate[0].OVERALL);
    }

    public async rtlChanged(event: boolean) {
        this.nomWebServicesJson.HelloResults.Rtl = event;
        this.store.dispatch(HelloResultsActions.setDirectionality({ rtl: this.nomWebServicesJson.HelloResults.Rtl }));
        await this.updatePersonalSettings();
    }

    public async mobileChanged(event: boolean) {
        this.pstNotificationSettings().Mobile.Recieve = event;
        await this.updateNotificationSettings();
    }

    public async textChanged(event: boolean | string) {
        if (typeof event === 'boolean') {
            this.pstNotificationSettings().Phone.Recieve = event;
        }
        if (typeof event === 'string') {
            this.pstNotificationSettings().Phone.Input = event;
        } else {
            this.pstNotificationSettings().Phone.Input = '';
        }

        await this.updateNotificationSettings();
    }

    public async timezoneChanged(event: LookupValue) {
        let timezone: TimeZone;
        if (event) {
            timezone = [...this.commonTimeZones(), ...this.ianaTimeZones()].find(item => item.id === event.id);
            this.globalTablesService.PersonalTimeZoneDisplay = timezone;
            this.nomWebServicesJson.HelloResults.PersonalTimeZone = event?.id?.toString();
            this.timeZoneValue.set(event);
        } else {
            this.nomWebServicesJson.HelloResults.PersonalTimeZone = '';
            this.timeZoneValue.set(null);
        }

        this.store.dispatch(
            GlobalTablesActions.updateTimeZoneSettings({
                personalTimeZone: timezone,
                personalTimeZoneDisplay: timezone,
            })
        );
        applyPersonalTimeZone(timezone?.id);
        await this.updatePersonalSettings();
    }

    public async languageChanged(event: ENPSLanguage) {
        if (event) {
            this.globalTablesService.PersonalLanguage = event;
            this.nomWebServicesJson.HelloResults.PersonalLanguage = event?.id?.toString();
            this.languageValue.set({ id: event.id, title: event.title });
            this.store.dispatch(HelloResultsActions.setLanguageSettings({ language: event?.id?.toString() }));
        } else {
            this.nomWebServicesJson.HelloResults.PersonalLanguage = '';
            this.languageValue.set(null);
            this.store.dispatch(HelloResultsActions.setLanguageSettings({ language: '' }));
        }

        await this.updatePersonalSettings();
    }

    public async weekDayChanged(event: { id: number; title: string }) {
        if (event) {
            this.nomWebServicesJson.HelloResults.DayOfWeek = event?.id;
        } else {
            this.nomWebServicesJson.HelloResults.DayOfWeek = this.weekDays[0]?.id;
        }
        updateLuxonWeekSettings(event?.id);
        this.store.dispatch(HelloResultsActions.setWeekDaySetting({ dayOfWeek: event?.id }));
        await this.updatePersonalSettings();
    }

    showAdminSettings() {
        this.dialog.closeAll();
        this.dialog.open(DiscoverAdminSettingsDialogComponent, {
            hasBackdrop: true,
            width: '35%',
            panelClass: 'st-discover-admin-settings-dialog-panel',
        });
    }

    async toggleDarkMode() {
        this.themeService.toggleDarkMode();
    }
}
